import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Music, Calendar, User, Users, Shield, CheckCircle, Globe, Phone, Mail, MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;
const SITE_URL = window.location.origin;

const PublicWorkPage = () => {
  const { id } = useParams();
  const [workData, setWorkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPublicWork();
  }, [id]);

  const loadPublicWork = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/public/works/${id}`);
      setWorkData(response.data);
    } catch (error) {
      console.error('Error loading public work:', error);
      
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Obra não encontrada ou não está disponível publicamente';
      
      if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getWorkTypeLabel = (type) => {
    const types = {
      letra: 'Letra',
      musica: 'Música',
      partitura: 'Partitura',
      letra_e_musica: 'Letra e Música',
      arranjo: 'Arranjo',
      producao: 'Produção',
      video: 'Vídeo'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Music className="w-16 h-16 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Carregando obra...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 shadow-lg text-center max-w-md">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Obra Não Disponível</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all">
            Voltar ao Início
          </Link>
        </div>
      </div>
    );
  }

  if (!workData) return null;

  const { work, author, certificate_hash, registration_date, verification_url } = workData;
  const pageUrl = `${SITE_URL}/obra/${id}`;
  const verificationFullUrl = `${SITE_URL}${verification_url}`;

  // Structured data for search engines (Schema.org)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": pageUrl,
    "name": work.title,
    "description": work.description || `${getWorkTypeLabel(work.work_type)} registrada na plataforma Minha Composição Autoral`,
    "text": work.lyrics,
    "genre": work.genre,
    "inLanguage": work.language || "pt-BR",
    "datePublished": registration_date,
    "author": work.coauthors && work.coauthors.length > 0 
      ? work.coauthors.map(co => ({
          "@type": "Person",
          "name": co.name,
          "roleName": co.role
        }))
      : {
          "@type": "Person",
          "name": author.name
        },
    "copyrightHolder": {
      "@type": "Person",
      "name": author.name
    },
    "copyrightYear": new Date(registration_date).getFullYear(),
    "publisher": {
      "@type": "Organization",
      "name": "Minha Composição",
      "url": "https://www.minhacomposicao.com.br"
    },
    "license": "All Rights Reserved",
    "workExample": {
      "@type": "DigitalDocument",
      "name": "Certificado de Registro Autoral",
      "url": verificationFullUrl,
      "encodingFormat": "application/pdf"
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{work.title} - Letra Registrada | Minha Composição</title>
        <meta name="description" content={`${work.title} - ${getWorkTypeLabel(work.work_type)} por ${author.name}. Letra completa registrada e protegida na plataforma Minha Composição Autoral. ${work.description || ''}`} />
        <meta name="keywords" content={`${work.title}, ${author.name}, letra de música, registro autoral, direitos autorais, ${work.genre || 'música'}, composição musical`} />
        <link rel="canonical" href={pageUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="music.song" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={`${work.title} - ${author.name}`} />
        <meta property="og:description" content={`Letra completa de "${work.title}" registrada na Minha Composição. ${getWorkTypeLabel(work.work_type)} - ${work.genre || 'Música'}`} />
        <meta property="og:site_name" content="Minha Composição" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={`${work.title} - ${author.name}`} />
        <meta name="twitter:description" content={`Letra completa registrada na Minha Composição`} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Robots - Allow indexing */}
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with Branding */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                🎵 MINHA COMPOSIÇÃO
              </h1>
              <p className="text-sm text-gray-600">Registro Público de Letras Musicais</p>
            </Link>
          </div>

          {/* Verified Badge */}
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-r-lg">
            <div className="flex items-center">
              <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
              <div>
                <p className="font-semibold text-green-800">Obra Registrada e Verificada</p>
                <p className="text-sm text-green-700">
                  Certificado digital com validade jurídica • Registro nº {id.substring(0, 8).toUpperCase()}
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">{work.title}</h1>
                  <p className="text-blue-100 text-lg">{getWorkTypeLabel(work.work_type)}</p>
                </div>
                <Globe className="w-12 h-12 opacity-50" />
              </div>
            </div>

            {/* Info Section */}
            <div className="p-8 border-b">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <User className="w-6 h-6 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Autor Principal</p>
                    <p className="font-semibold text-gray-900">{author.name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-6 h-6 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Data de Registro</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(registration_date).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {work.genre && (
                  <div className="flex items-start gap-3">
                    <Music className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Gênero Musical</p>
                      <p className="font-semibold text-gray-900">{work.genre}</p>
                    </div>
                  </div>
                )}

                {work.coauthors && work.coauthors.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Users className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Coautores</p>
                      <p className="font-semibold text-gray-900">{work.coauthors.length} coautor(es)</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              {work.description && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-gray-700">{work.description}</p>
                </div>
              )}

              {/* Coauthors List */}
              {work.coauthors && work.coauthors.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Coautores da Obra:</h3>
                  <div className="space-y-2">
                    {work.coauthors.map((coauthor, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{coauthor.name}</p>
                          <p className="text-sm text-gray-600">{coauthor.role}</p>
                        </div>
                        <span className="text-blue-600 font-bold">{coauthor.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Lyrics Section */}
            {work.lyrics && (
              <div className="p-8 bg-gradient-to-br from-purple-50 to-blue-50">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  🎼 Letra Completa da Música
                </h2>
                <div className="bg-white p-8 rounded-lg shadow-inner border-2 border-purple-200">
                  <div className="whitespace-pre-wrap text-gray-800 leading-relaxed text-lg font-serif">
                    {work.lyrics}
                  </div>
                </div>
              </div>
            )}

            {/* Contact Author Section */}
            {(author.email || author.phone) && (
              <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-t">
                <div className="max-w-2xl mx-auto">
                  <div className="text-center mb-6">
                    <User className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Entre em Contato com o Autor
                    </h3>
                    <p className="text-gray-600">
                      Gostou desta obra? Entre em contato com {author.artistic_name || author.name}
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="space-y-4">
                      {/* Email */}
                      {author.email && (
                        <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all">
                          <div className="flex-shrink-0">
                            <Mail className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-700">E-mail</p>
                            <a 
                              href={`mailto:${author.email}`}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {author.email}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Phone / WhatsApp */}
                      {author.phone && (
                        <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-all">
                          <div className="flex-shrink-0">
                            <MessageCircle className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-700">WhatsApp / Telefone</p>
                            <div className="flex items-center gap-2">
                              <a 
                                href={`https://wa.me/${author.phone.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-800 font-medium"
                              >
                                {author.phone}
                              </a>
                              <span className="text-xs text-gray-500">(Clique para abrir no WhatsApp)</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-medium mb-1">💡 Dica de Segurança:</p>
                          <p>Ao negociar direitos autorais, sempre utilize contratos formais e consulte um advogado especializado.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Certificate Section */}
            {certificate_hash && (
              <div className="p-8 bg-gradient-to-r from-green-50 to-blue-50 border-t">
                <div className="text-center">
                  <Shield className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Certificado de Registro Autoral
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Esta obra está protegida por certificado digital com validade jurídica
                  </p>
                  <div className="bg-white p-4 rounded-lg mb-4 inline-block">
                    <p className="text-xs text-gray-500 mb-1">Hash do Certificado:</p>
                    <code className="text-sm font-mono text-gray-800 break-all">{certificate_hash}</code>
                  </div>
                  <div>
                    <Link
                      to={verification_url}
                      className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
                    >
                      🔍 Verificar Autenticidade do Certificado
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="p-6 bg-gray-50 text-center border-t">
              <p className="text-sm text-gray-600 mb-2">
                ⚖️ Esta obra está protegida pela <strong>Lei nº 9.610/98 (Lei de Direitos Autorais)</strong>
              </p>
              <p className="text-xs text-gray-500">
                Registro realizado na plataforma Minha Composição Autoral • Todos os direitos reservados ao(s) autor(es)
              </p>
              <div className="mt-4">
                <Link
                  to="/"
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  🎵 Registre também sua composição →
                </Link>
              </div>
            </div>
          </div>

          {/* SEO Footer Text */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              Registro público de letra musical indexado pelos principais motores de busca.
              Esta página é acessível ao Google, Bing, GPT e outras plataformas de IA para
              garantir visibilidade e proteção autoral da obra.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicWorkPage;
